import React, { useEffect, useState } from "react";
import api from "../api/Api"; 
import { Route } from "lucide-react";

const MyTrips = () => {
  const getToday = () => {
    const d = new Date();
    return d.toISOString().split("T")[0];
  };

  const getYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  };

  // --- DATE FORMATTER ---
  // Converts "2026-03-22" into "22 Mar 2026"
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-GB", options);
  };

  // --- DRIVER NAME FORMATTER ---
  const formatDriverName = (fName, mName, lName) => {
    // Filter out null/undefined/empty spaces and join with a space
    const parts = [fName, mName, lName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "Unassigned";
  };

  // --- STATUS BADGE FORMATTER ---
  const getStatusBadge = (status) => {
    if (!status) return "bg-secondary";
    const s = status.toLowerCase();
    if (s === "completed") return "bg-success";
    if (s === "running") return "bg-warning text-dark";
    return "bg-secondary"; // For pending or unknown
  };

  const [filters, setFilters] = useState({
    fromDate: getYesterday(),
    toDate: getToday(),
    enquiryNo: "",
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const fetchTrips = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const res = await api.get("/tripdetails/my-trips", {
        params: currentFilters,
      });

      if (res.data.success) {
        setData(res.data.data);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  const handleClear = () => {
    const resetFilters = {
      fromDate: getYesterday(),
      toDate: getToday(),
      enquiryNo: "",
    };
    setFilters(resetFilters);
    fetchTrips(resetFilters);
  };

  return (
    <div className="container-fluid py-3" style={{ maxWidth: "1400px" }}>
      
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          {/* Icon with a soft background for a modern admin look */}
          <div className="bg-primary bg-opacity-10 p-2 rounded-3 border border-primary border-opacity-25">
            <Route className="text-primary" size={24} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold text-dark">Assigned Trips</h5>
            <small className="text-muted">Monitor and manage driver trip statuses and details</small>
          </div>
        </div>
        
        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary px-3 py-2 rounded-pill shadow-sm">
          {data.length} Total Trips
        </span>
      </div>

      {/* FILTER CARD (Compact Bootstrap) */}
      <div className="card shadow-sm border-0 mb-3 bg-light">
        <div className="card-body p-2 p-md-3">
          <div className="row g-2 align-items-end">
            
            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-secondary mb-1">From Date</label>
              <input
                type="date"
                className="form-control form-control-sm shadow-none"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleChange}
              />
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label small fw-bold text-secondary mb-1">To Date</label>
              <input
                type="date"
                className="form-control form-control-sm shadow-none"
                name="toDate"
                value={filters.toDate}
                onChange={handleChange}
              />
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label small fw-bold text-secondary mb-1">Enquiry No</label>
              <input
                type="text"
                className="form-control form-control-sm shadow-none"
                name="enquiryNo"
                value={filters.enquiryNo}
                placeholder="Search by Enquiry No..."
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchTrips();
                }}
              />
            </div>

            <div className="col-6 col-md-2">
              <button 
                className="btn btn-primary btn-sm w-100 fw-bold shadow-sm" 
                onClick={() => fetchTrips()}
              >
                Search
              </button>
            </div>
            
            <div className="col-6 col-md-2">
              <button 
                className="btn btn-outline-secondary btn-sm w-100 fw-bold bg-white" 
                onClick={handleClear}
              >
                Clear
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* TABLE CARD (Compact Bootstrap) */}
      <div className="card shadow-sm border-0">
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle mb-0" style={{ fontSize: "0.85rem" }}>
            <thead className="table-light text-secondary">
              <tr>
                <th className="py-2 px-3 fw-semibold">#</th>
                <th className="py-2 fw-semibold">Enq / Conf. ID</th>
                <th className="py-2 fw-semibold">Journey Date</th>
                <th className="py-2 fw-semibold">Driver Details</th>
                <th className="py-2 fw-semibold">Customer Name</th>
                <th className="py-2 fw-semibold">Pickup Time</th>
                <th className="py-2 fw-semibold">Drop Time</th>
                <th className="py-2 fw-semibold text-center">Start KM</th>
                <th className="py-2 fw-semibold text-center">End KM</th>
                <th className="py-2 fw-semibold text-center">Total KM</th>
                {/* <th className="py-2 fw-semibold">Itinerary</th> */}
                <th className="py-2 fw-semibold text-center">Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center py-4 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Loading trips...
                  </td>
                </tr>
              ) : data.length > 0 ? (
                data.map((item, index) => (
                  <tr key={item.Ts_id || index}>
                    <td className="px-3 text-muted">{index + 1}</td>
                    
                    {/* ENQUIRY ID & CONFIRMATION ID */}
                    <td className="text-nowrap">
                      <div className="fw-bold text-primary">{item.enquiryNo}</div>
                      {item.confirmation_id && (
                        <div className="badge bg-info text-dark mt-1 border" style={{ fontSize: "0.7rem", fontWeight: "600" }}>
                          {item.confirmation_id}
                        </div>
                      )}
                    </td>
                    
                    {/* JOURNEY DATE */}
                    <td className="text-nowrap">{formatDate(item.journeyDate)}</td>

                    {/* DRIVER DETAILS (Placed before Customer Name) */}
                    <td className="text-nowrap">
                      <div className="fw-semibold text-dark">
                        {formatDriverName(item.driver_fstname, item.driver_mdlname, item.driver_lstname)}
                      </div>
                      {item.driver_regno && (
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                          {item.driver_regno}
                        </div>
                      )}
                    </td>
                    
                    {/* CUSTOMER NAME */}
                    <td className="fw-medium">{item.customer_name}</td>
                    
                    <td className="text-nowrap">{item.pickupTime || "-"}</td>
                    <td className="text-nowrap">{item.pickupEnd || "-"}</td>
                    <td className="text-center">{item.start_km || 0}</td>
                    <td className="text-center">{item.end_km || 0}</td>
                    <td className="text-center fw-bold text-primary bg-light">{item.totalKm}</td>
                    
                    {/* ITINERARY (Checking both exact SQL case and lowercase just in case) */}
                    {/* <td className="text-muted small text-truncate" style={{ maxWidth: "150px" }}>
                      {item.Iteniarary_Details || item.itinerary_details || "-"}
                    </td> */}
                    
                    {/* STATUS */}
                    <td className="text-center">
                      <span className={`badge ${getStatusBadge(item.status)}`}>
                        {item.status || "Pending"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-4 text-muted">
                    No trips found for the selected criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
    </div>
  );
};

export default MyTrips;